import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { useLanguage } from '../i18n';
import { STANDARD_ERROR_MESSAGE } from '../utils/userError';

/**
 * The chat's error state (Error Clean up #439).
 *
 * A failed turn used to render as an ordinary reply whose TEXT was the raw
 * exception ("I encountered an error while processing your request: 504
 * DEADLINE_EXCEEDED. {'error': …}"). It is now a small, clearly-marked notice
 * inside the assistant bubble, BELOW whatever text had already streamed (that
 * text is kept — #328: a stream cannot be replayed, so delivered words stay).
 *
 *   technical  → exactly "An error occurred, please retry." + a Retry button
 *                that re-sends the turn's user message;
 *   functional → the backend's own sentence (e.g. "PAI's energy is empty."),
 *                no Retry — retrying would hit the same wall.
 *
 * `streamError` is `message.metadata.stream_error`:
 *   { kind: 'technical'|'functional', type, message, ref }
 * The ref is a support correlation id; it is exposed as a data attribute and a
 * tooltip, never in the sentence.
 */
export default function ChatErrorNotice({ streamError, onRetry = null, retryDisabled = false }) {
    const { t } = useLanguage();
    if (!streamError) return null;
    const functional = streamError.kind === 'functional' && !!streamError.message;
    const text = functional
        ? streamError.message
        : t('errorRetry', STANDARD_ERROR_MESSAGE);
    const ref = streamError.ref || null;
    return (
        <div
            data-testid="chat-error-notice"
            data-error-kind={functional ? 'functional' : 'technical'}
            data-error-ref={ref || undefined}
            role="alert"
            title={ref ? t('errorRefLabel', 'Ref: {ref}', { ref }) : undefined}
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0 max-w-full rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[13px] text-rose-100"
        >
            <AlertTriangle size={14} className="shrink-0 text-rose-300" />
            <span data-testid="chat-error-message" className="min-w-0 break-words [overflow-wrap:anywhere]">
                {text}
            </span>
            {!functional && onRetry && (
                <button
                    type="button"
                    data-testid="chat-error-retry"
                    onClick={onRetry}
                    disabled={retryDisabled}
                    className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-rose-300/40 px-2 py-0.5 text-[12px] font-medium text-rose-100 hover:bg-rose-400/15 disabled:opacity-50"
                >
                    <RotateCcw size={12} className="shrink-0" />
                    {t('commonRetry', 'Retry')}
                </button>
            )}
        </div>
    );
}
